import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Row, Col, Alert, Spinner, Button, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faArrowLeft, faInfoCircle, faSearch } from "@fortawesome/free-solid-svg-icons";
import MainLayouts from "layouts/MainLayouts";
import CardWrapper from "components/CardWrapper";
import { getAddonOptions, getClientServices, setSelectedService, setSelectedAddon, resetAddonSelection } from "features/addon/addonSlice";
import { addToCart } from "features/common/layouts/cartSlice";
import { formatIDR } from "helpers/utils";
import { notifyError, notifySuccess } from "components/ToastMessage";
import './style.css';

// Helper function to get billing cycle label
const getBillingCycleLabel = (cycle) => {
  const labels = {
    'monthly': '1 Bulan',
    'quarterly': '3 Bulan',
    'semiannually': '6 Bulan',
    'semi-annually': '6 Bulan',
    'annually': '1 Tahun',
    'biennially': '2 Tahun',
    'triennially': '3 Tahun'
  };

  const normalizedCycle = String(cycle).toLowerCase().trim();
  return labels[normalizedCycle] || normalizedCycle;
};

// Helper function to calculate current price for services
const calculateCurrentPrice = (service) => {
  // Try to get price from service amount fields first
  const amount = parseFloat(service?.amount || 0);
  const recurringamount = parseFloat(service?.recurringamount || 0);
  const firstpaymentamount = parseFloat(service?.firstpaymentamount || 0);

  // Use the first non-zero amount available
  if (amount > 0) return amount;
  if (recurringamount > 0) return recurringamount;
  if (firstpaymentamount > 0) return firstpaymentamount;

  // Fallback to product_details pricing if available
  if (service?.product_details?.pricing?.IDR) {
    const billingCycle = String(service.billingcycle).toLowerCase().trim();
    const pricing = service.product_details.pricing.IDR;
    const price = parseFloat(pricing[billingCycle] || 0);
    if (price > 0) return price;
  }

  return 0;
};

// Helper function to get addon price
const getAddonPrice = (addon, serviceBillingCycle = null) => {
  // Try IDR first (currency ID 1), then USD (currency ID 3)
  const idrPricing = addon.pricing?.["1"];
  const usdPricing = addon.pricing?.["3"];
  const pricing = idrPricing || usdPricing;
  
  if (pricing) {
    let price = 0;
    
    // For recurring addons, try to get price for specific billing cycle
    if (!addon.is_one_time && serviceBillingCycle) {
      const cycleKey = serviceBillingCycle.toLowerCase();
      price = pricing[cycleKey];
      
      if (price !== undefined && price >= 0) {
        if (price === 0) {
          return {
            price: 0,
            currency: idrPricing ? 'IDR' : 'USD',
            formatted: 'GRATIS'
          };
        }
        return {
          price: price,
          currency: idrPricing ? 'IDR' : 'USD',
          formatted: idrPricing ? formatIDR(price) : `$${price}`
        };
      }
    }
    
    // Fallback: get minimum price from all available cycles
    const prices = Object.values(pricing).filter(p => p >= 0);
    if (prices.length > 0) {
      const minPrice = Math.min(...prices);
      if (minPrice === 0) {
        return {
          price: 0,
          currency: idrPricing ? 'IDR' : 'USD',
          formatted: 'GRATIS'
        };
      }
      return {
        price: minPrice,
        currency: idrPricing ? 'IDR' : 'USD',
        formatted: idrPricing ? formatIDR(minPrice) : `$${minPrice}`
      };
    }
  }
  return { price: 0, currency: 'IDR', formatted: 'GRATIS' };
};

// Helper function to format addon price display
const formatAddonPrice = (price, formatted) => {
  if (formatted === 'GRATIS') {
    return (
      <span className="text-success fw-bold">
        {formatted}
      </span>
    );
  }
  return (
    <span style={{ color: '#ff851b' }}>
      {formatted}
    </span>
  );
};

// Helper function to filter addons based on service billing cycle and active status
const filterAddonsByBillingCycle = (addons, serviceBillingCycle, activeAddons = []) => {
  if (!serviceBillingCycle) return addons;
  
  return addons.filter(addon => {
    // Don't show addons that are already active
    if (addon.is_active || activeAddons.some(active => active.addon_id === addon.id)) {
      return false;
    }
    
    // Always show one-time addons
    if (addon.is_one_time) return true;
    
    // For recurring addons, check if they have pricing for the service billing cycle
    const idrPricing = addon.pricing?.["1"];
    const usdPricing = addon.pricing?.["3"];
    const pricing = idrPricing || usdPricing;
    
    if (pricing) {
      const cycleKey = serviceBillingCycle.toLowerCase();
      const price = pricing[cycleKey];
      return price !== undefined && price >= 0; // Show if price exists and is not -1
    }
    
    return false;
  });
};


// Helper function to get category color
const getCategoryColor = (category) => {
  switch (category) {
    case 'Support':
      return 'primary';
    case 'Backup':
      return 'info';
    case 'WHMCS License':
      return 'dark';
    case 'Merchandise':
      return 'warning';
    case 'Additional Features':
      return 'success';
    case 'Plus Pack':
      return 'secondary';
    case 'Web Builder':
      return 'light';
    case 'SSL Certificate':
      return 'success';
    case 'Monitoring':
      return 'warning';
    case 'Security':
      return 'danger';
    case 'Storage':
      return 'primary';
    case 'Bandwidth':
      return 'secondary';
    case 'Email':
      return 'dark';
    case 'Domain':
      return 'light';
    default:
      return 'outline-secondary';
  }
};

// Helper function to check if addon is in cart
const isAddonInCart = (addon, cartProducts, currentService) => {
  if (!currentService || !cartProducts) return false;
  
  return cartProducts.some(product => 
    product.type === 'addon' && 
    product.pid === addon.id && 
    product.serviceid === (currentService.serviceid || currentService.id)
  );
};

const Addon = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    currentService,
    availableAddons,
    activeAddons,
    clientServices,
    selectedService,
    status,
    clientServicesStatus,
    error,
  } = useSelector((state) => state.addon);

  // Get cart state to check which addons are already in cart
  const cartProducts = useSelector((state) => state.cart.productsList);

  // Auth state
  const isAuthenticated = useSelector((state) => state.auth.status_login);

  const [serviceid, setServiceid] = useState(null);
  const [showAllServices, setShowAllServices] = useState(false);
  const [serviceSearchQuery, setServiceSearchQuery] = useState('');
  const [selectedAddons, setSelectedAddons] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("serviceid");

    if (sid) {
      // If serviceid is provided, directly fetch addon options
      setServiceid(sid);
      // Only fetch if user is authenticated
      if (isAuthenticated) {
      dispatch(getAddonOptions(sid));
      }
    } else {
      // If no serviceid, try to get client services if user is logged in
      if (isAuthenticated) {
      dispatch(getClientServices());
    }
    }
  }, [location.search, dispatch, isAuthenticated]);

  // Reload data when user logs in (only if no serviceid in URL)
  useEffect(() => {
    if (isAuthenticated && !serviceid) {
      // Only fetch client services if no specific serviceid is set
        dispatch(getClientServices());
    }
  }, [isAuthenticated, dispatch, serviceid]);

  useEffect(() => {
    if (error) {
      notifyError(error);
    }
  }, [error]);

  const handleServiceSelect = (service) => {
    // Check if service is active
    if (service.status !== 'Active') {
      notifyError("Hanya service dengan status Active yang dapat ditambahkan addon");
      return;
    }

    dispatch(setSelectedService(service));
    // Use 'id' field instead of 'serviceid' as per the API response structure
    const serviceId = service.id || service.serviceid;
    if (!serviceId) {
      notifyError("Invalid service ID");
      return;
    }
    setServiceid(serviceId.toString());
    setSelectedAddons([]); // Reset selected addons when changing service
    dispatch(getAddonOptions(serviceId.toString()));
  };

  const handleAddonSelect = (addon) => {
    // Don't allow selection if addon is already in cart
    if (isAddonInCart(addon, cartProducts, currentService)) {
      return;
    }
    
    setSelectedAddons(prev => {
      const isSelected = prev.some(selected => selected.id === addon.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== addon.id);
      } else {
        return [...prev, addon];
      }
    });
    dispatch(setSelectedAddon(addon));
  };


  if (status === "loading" || clientServicesStatus === "loading") {
    return (
      <MainLayouts>
        <div className="text-center py-5">
          <div className="mb-4">
            <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
              <Spinner animation="border" size="lg" className="text-primary" />
            </div>
            <h5 className="text-muted">Memuat Data Addon</h5>
            <p className="text-muted">Mohon tunggu sebentar...</p>
          </div>
        </div>
      </MainLayouts>
    );
  }

  return (
    <MainLayouts summary={false}>
      <div className="addon-page">
        {/* Service Selection - Show when no serviceid provided and user has services */}
        {!serviceid && clientServices.length > 0 && (
          <CardWrapper
            title="Pilih Layanan untuk Addon"
            titleExtra={
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowAllServices(!showAllServices)}
              >
                {showAllServices ? 'Sembunyikan Non-Active' : 'Tampilkan Semua'}
              </Button>
            }
          >
            {/* Search Input */}
            <div className="mb-3">
              <div className="position-relative">
                <FontAwesomeIcon 
                  icon={faSearch} 
                  className="position-absolute"
                  style={{ left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', zIndex: 10 }}
                />
                <input
                  type="text"
                  className="form-control ps-5"
                  placeholder="Cari layanan berdasarkan nama atau domain..."
                  value={serviceSearchQuery}
                  onChange={(e) => setServiceSearchQuery(e.target.value)}
                  style={{ fontSize: '14px' }}
                />
                {serviceSearchQuery && (
                  <button
                    className="btn btn-sm position-absolute"
                    style={{ right: '5px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#6c757d' }}
                    onClick={() => setServiceSearchQuery('')}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {(() => {
              const filteredServices = clientServices.filter(service => {
                // Filter by status
                const statusMatch = showAllServices || service.status === 'Active';
                
                // Filter by search query
                if (!serviceSearchQuery.trim()) {
                  return statusMatch;
                }
                
                const query = serviceSearchQuery.toLowerCase().trim();
                const serviceName = (service.name || service.productname || '').toLowerCase();
                const domain = (service.domain || '').toLowerCase();
                
                return statusMatch && (serviceName.includes(query) || domain.includes(query));
              });

              if (filteredServices.length === 0) {
                return (
                  <div className="text-center py-5">
                    <FontAwesomeIcon icon={faSearch} className="text-muted mb-2" style={{ fontSize: '48px' }} />
                    <p className="text-muted mb-0">
                      {serviceSearchQuery ? (
                        <>Tidak ada layanan yang cocok dengan "<strong>{serviceSearchQuery}</strong>"</>
                      ) : (
                        'Tidak ada layanan yang tersedia'
                      )}
                    </p>
                    {serviceSearchQuery && (
                      <Button
                        variant="link"
                        size="sm"
                        className="mt-2"
                        onClick={() => setServiceSearchQuery('')}
                      >
                        Hapus pencarian
                      </Button>
                    )}
                  </div>
                );
              }

              return (
                <Row className="g-4">
                  {filteredServices.map((service, index) => {
                  const isActive = service.status === 'Active';
                  const isMonitime = service.pid === 1146;
                  const hasDomain = service.domain && service.domain.trim() !== '';
                  return (
                    <Col key={index} lg={6} md={12}>
                      <Card
                        className={`h-100 service-card ${isActive ? 'cursor-pointer' : 'cursor-not-allowed'} ${selectedService?.id === service.id ? 'border-primary border-3' : ''} ${!isActive ? 'opacity-60' : ''}`}
                        onClick={() => isActive && handleServiceSelect(service)}
                        style={{
                          pointerEvents: isActive ? 'auto' : 'none',
                          backgroundColor: !isActive ? '#f8f9fa' : 'white'
                        }}
                      >
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-bold service-product-name">
                                {service.name || service.productname}
                              </h6>
                              <div className="d-flex align-items-center gap-1">
                                {hasDomain ? (
                                  <span className="badge service-domain-badge service-domain-orange">{service.domain}</span>
                                ) : !isMonitime ? (
                                  <span className="badge bg-secondary service-domain-badge">Belum memiliki domain</span>
                                ) : null}
                                <span className={`badge ${
                                  isActive 
                                    ? 'bg-success' 
                                    : service.status?.toLowerCase() === 'terminated' 
                                      ? 'service-status-terminated' 
                                      : 'bg-secondary'
                                } service-status-badge`}>
                                  {service.status}
                                </span>
                              </div>
                            </div>
                            {selectedService?.id === service.id && (
                              <div className="text-primary ms-2">
                                <FontAwesomeIcon icon={faPlus} />
                              </div>
                            )}
                          </div>

                          <div className="row g-2">
                            <div className="col-6">
                              <div className="bg-light p-2 rounded-3">
                                <small className="text-muted d-block">Termin</small>
                                <span className="fw-bold">{getBillingCycleLabel(service.billingcycle)}</span>
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="bg-light p-2 rounded-3">
                                <small className="text-muted d-block">Harga</small>
                                <span className="fw-bold" style={{ color: '#ff851b' }}>{formatIDR(calculateCurrentPrice(service))}</span>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="bg-light p-2 rounded-3">
                                <small className="text-muted d-block">Jatuh Tempo</small>
                                <span className="fw-bold">{service.nextduedate}</span>
                              </div>
                            </div>
                          </div>

                          {isActive && (
                            <div className="mt-2 text-center">
                              <small className="text-success">
                                <FontAwesomeIcon icon={faPlus} className="me-1" />
                                Klik untuk tambah addon
                              </small>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                  })}
                </Row>
              );
            })()}
            {clientServices.some(service => service.status !== 'Active') && (
              <div className="mt-4 text-center">
                <small className="text-muted">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                  {showAllServices ? (
                    <>
                      Menampilkan semua layanan ({clientServices.length} total)
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 ms-1"
                        onClick={() => setShowAllServices(false)}
                      >
                        Sembunyikan non-Active
                      </Button>
                    </>
                  ) : (
                    <>
                      {clientServices.filter(service => service.status !== 'Active').length} layanan non-Active tersembunyi.
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 ms-1"
                        onClick={() => setShowAllServices(true)}
                      >
                        Tampilkan semua
                      </Button>
                    </>
                  )}
                </small>
              </div>
            )}
          </CardWrapper>
        )}

        {/* Show message when no services available */}
        {!serviceid && clientServices.length === 0 && clientServicesStatus === "idle" && (
          <CardWrapper title="Addon Layanan">
            <div className="text-center py-5">
              <div className="mb-4">
                <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                  <FontAwesomeIcon icon={faPlus} size="2x" className="text-muted" />
                </div>
                <h5 className="text-muted">
                  {!isAuthenticated ? 'Login untuk Mengakses Addon' : 'Tidak Ada Layanan Tersedia'}
                </h5>
                <p className="text-muted">
                  {!isAuthenticated
                    ? 'Untuk mengakses fitur Addon silahkan Login terlebih dahulu...'
                    : 'Tidak ada layanan yang dapat ditambahkan addon saat ini.'
                  }
                </p>
              </div>
            </div>
          </CardWrapper>
        )}

        {/* Show error message for client services */}
        {!serviceid && clientServicesStatus === "error" && (
          <CardWrapper title="Addon Layanan">
            <div className="text-center py-5">
              <div className="mb-4">
                <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                  <FontAwesomeIcon icon={faInfoCircle} size="2x" className="text-warning" />
                </div>
                <h5 className="text-muted">Login Diperlukan</h5>
                <p className="text-muted">Untuk mengakses fitur Addon silahkan Login terlebih dahulu...</p>
              </div>
            </div>
          </CardWrapper>
        )}

        {/* Current Service Information */}
        {currentService && serviceid && (
          <CardWrapper
            title="Detail Layanan"
            titleExtra={
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  dispatch(resetAddonSelection());
                  setServiceid(null);
                  setShowAllServices(false);
                  navigate('/addon');
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                Pilih Layanan Lain
              </Button>
            }
          >
            {/* Show error for non-active services */}
            {currentService.status !== 'Active' && (
              <Alert variant="danger" className="mb-4">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                <strong>Status Service: {currentService.status}</strong> - Hanya service dengan status Active yang dapat ditambahkan addon.
              </Alert>
            )}

            <div className="row">
              <div className="col-md-12">
                <div className="service-header mb-4">
                  <div className="mb-3">
                      <h4 className="mb-1 fw-bold text-dark">
                        {currentService.name || currentService.productname}
                      </h4>
                      {currentService.domain && currentService.domain.trim() !== '' && (
                        <p className="text-muted mb-1">{currentService.domain}</p>
                      )}
                      <span className={`badge ${currentService.status === 'Active' ? 'bg-success' : 'bg-warning'} fs-6`}>
                        {currentService.status}
                      </span>
                  </div>
                </div>

                <div className="service-details">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="detail-card">
                        <div className="detail-label">Termin Pembayaran</div>
                        <div className="detail-value">{getBillingCycleLabel(currentService.billingcycle)}</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="detail-card">
                        <div className="detail-label">Harga Layanan</div>
                        <div className="detail-value price-highlight">{formatIDR(calculateCurrentPrice(currentService))}</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="detail-card">
                        <div className="detail-label">Jatuh Tempo</div>
                        <div className="detail-value">{currentService.nextduedate}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardWrapper>
        )}

        {/* Active Addons - Only show if there are active addons */}
        {currentService && serviceid && currentService.status === 'Active' && activeAddons.length > 0 && (
          <CardWrapper title="Addon yang Sudah Terpasang">
              <div className="list-group">
                {activeAddons.map((addon, index) => (
                  <div key={index} className="list-group-item addon-list-item">
                    <div className="row g-0 align-items-center">
                      <div className="col">
                        <div className="d-flex flex-column">
                          <div className="d-flex align-items-center mb-1">
                            <h6 className="mb-0 fw-bold me-2 addon-name">{addon.name}</h6>
                            <Badge bg="success" className="flex-shrink-0">Aktif</Badge>
                          </div>
                          <small className="text-muted addon-description">{addon.description}</small>
                        </div>
                      </div>
                      <div className="col-auto">
                      <div className="addon-price text-end">
                        <div className="fw-bold mb-1">
                          {(() => {
                            const amount = addon.is_one_time ? 
                              (addon.addon_amount || 0) : 
                              (addon.addon_recurring_amount || 0);
                            const formatted = amount === 0 ? 'GRATIS' : formatIDR(amount);
                            return formatAddonPrice(amount, formatted);
                          })()}
                          </div>
                          <div className="small text-muted">
                          {addon.is_one_time ? 'Sekali Bayar' : getBillingCycleLabel(currentService.billingcycle)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
          </CardWrapper>
        )}

        {/* Available Addons */}
        {currentService && serviceid && currentService.status === 'Active' && Object.keys(availableAddons).length > 0 && (
          <CardWrapper
            title="Pilih Addon yang Diinginkan"
            titleExtra={
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  dispatch(resetAddonSelection());
                  setServiceid(null);
                  setShowAllServices(false);
                  navigate('/addon');
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                Kembali
              </Button>
            }
          >
            <div className="accordion" id="addonAccordion">
              {Object.entries(availableAddons).map(([category, addons], categoryIndex) => {
                // Filter addons based on service billing cycle and active status
                const filteredAddons = filterAddonsByBillingCycle(addons, currentService.billingcycle, activeAddons);
                
                // Skip category if no addons after filtering
                if (filteredAddons.length === 0) return null;
                
                return (
                  <div key={category} className="accordion-item">
                    <h2 className="accordion-header" id={`heading${categoryIndex}`}>
                      <button
                        className="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#collapse${categoryIndex}`}
                        aria-expanded="false"
                        aria-controls={`collapse${categoryIndex}`}
                      >
                        <span className="me-3 fw-bold">{category}</span>
                        <Badge bg={getCategoryColor(category)}>{filteredAddons.length} addon</Badge>
                      </button>
                    </h2>
                    <div
                      id={`collapse${categoryIndex}`}
                      className="accordion-collapse collapse"
                      aria-labelledby={`heading${categoryIndex}`}
                      data-bs-parent="#addonAccordion"
                    >
                      <div className="accordion-body p-0">
                        <div className="list-group list-group-flush">
                          {filteredAddons.map((addon, index) => (
                              <div 
                                key={index} 
                                className={`list-group-item addon-list-item ${addon.is_active || isAddonInCart(addon, cartProducts, currentService) ? 'bg-light' : 'cursor-pointer'}`}
                                onClick={() => !addon.is_active && !isAddonInCart(addon, cartProducts, currentService) && handleAddonSelect(addon)}
                                style={{ 
                                  cursor: addon.is_active || isAddonInCart(addon, cartProducts, currentService) ? 'not-allowed' : 'pointer',
                                  opacity: addon.is_active || isAddonInCart(addon, cartProducts, currentService) ? 0.7 : 1 
                                }}
                              >
                                <div className="row g-0 align-items-center">
                                  <div className="col-auto">
                                    <div className="form-check me-3">
                                      <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        checked={selectedAddons.some(selected => selected.id === addon.id) || isAddonInCart(addon, cartProducts, currentService)}
                                        onChange={() => !addon.is_active && handleAddonSelect(addon)}
                                        disabled={addon.is_active || isAddonInCart(addon, cartProducts, currentService)}
                                      />
                                    </div>
                                  </div>
                                  <div className="col">
                                    <div className="d-flex flex-column">
                                      <div className="d-flex align-items-center mb-1">
                                        <h6 className="mb-0 fw-bold me-2 addon-name">
                                          {addon.name}
                                        </h6>
                                        {addon.is_active && (
                                          <Badge bg="success" className="flex-shrink-0">Sudah Aktif</Badge>
                                        )}
                                        {!addon.is_active && isAddonInCart(addon, cartProducts, currentService) && (
                                          <Badge bg="info" className="flex-shrink-0">Di Cart</Badge>
                                        )}
                                      </div>
                                      <small className="text-muted addon-description">
                                        {addon.description}
                                      </small>
                                    </div>
                                  </div>
                                  <div className="col-auto">
                                    <div className="addon-price text-end">
                                      <div className="fw-bold mb-1" style={{ fontSize: '1rem' }}>
                                        {(() => {
                                          const priceInfo = getAddonPrice(addon, currentService.billingcycle);
                                          return formatAddonPrice(priceInfo.price, priceInfo.formatted);
                                        })()}
                                      </div>
                                      <div className="small text-muted">
                                        {addon.is_one_time ? 'Sekali Bayar' : getBillingCycleLabel(currentService.billingcycle)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardWrapper>
        )}

        {/* Selected Addons Summary */}
        {(selectedAddons.length > 0 || (currentService && serviceid && currentService.status === 'Active' && Object.keys(availableAddons).length > 0 && Object.values(availableAddons).flat().some(addon => isAddonInCart(addon, cartProducts, currentService)))) && currentService && serviceid && currentService.status === 'Active' && (
          <CardWrapper title="Addon yang Dipilih">
            <div className="list-group mb-3">
              {/* Show addons that are in cart */}
              {Object.values(availableAddons).flat().filter(addon => isAddonInCart(addon, cartProducts, currentService)).map((addon, index) => (
                <div key={`cart-${index}`} className="list-group-item addon-list-item">
                  <div className="row g-0 align-items-center">
                    <div className="col">
                      <div className="d-flex flex-column">
                        <div className="d-flex align-items-center mb-1">
                          <h6 className="mb-0 fw-bold addon-name me-2">{addon.name}</h6>
                          <Badge bg="info" className="flex-shrink-0">Di Cart</Badge>
                        </div>
                        <small className="text-muted addon-description">{addon.description}</small>
                      </div>
                    </div>
                    <div className="col-auto">
                      <div className="addon-price text-end">
                        <div className="fw-bold mb-1">
                          {(() => {
                            const priceInfo = getAddonPrice(addon, currentService.billingcycle);
                            return formatAddonPrice(priceInfo.price, priceInfo.formatted);
                          })()}
                    </div>
                        <div className="small text-muted">
                          {addon.is_one_time ? 'Sekali Bayar' : getBillingCycleLabel(currentService.billingcycle)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Show newly selected addons */}
              {selectedAddons.map((addon, index) => (
                <div key={`selected-${index}`} className="list-group-item addon-list-item">
                  <div className="row g-0 align-items-center">
                    <div className="col">
                      <div className="d-flex flex-column">
                        <h6 className="mb-1 fw-bold addon-name">{addon.name}</h6>
                        <small className="text-muted addon-description">{addon.description}</small>
                      </div>
                    </div>
                    <div className="col-auto">
                      <div className="addon-price text-end">
                        <div className="fw-bold mb-1">
                          {(() => {
                            const priceInfo = getAddonPrice(addon, currentService.billingcycle);
                            return formatAddonPrice(priceInfo.price, priceInfo.formatted);
                          })()}
                        </div>
                        <div className="small text-muted">
                          {addon.is_one_time ? 'Sekali Bayar' : getBillingCycleLabel(currentService.billingcycle)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <strong>Total Addon Dipilih: {selectedAddons.length + Object.values(availableAddons).flat().filter(addon => isAddonInCart(addon, cartProducts, currentService)).length}</strong>
              </div>
              <div>
                <button
                  onClick={() => setSelectedAddons([])}
                  className="btn btn-outline-secondary btn-sm hover-shadow rounded-5 px-4"
                >
                  <p className="mb-0 fw-bold">Hapus Semua</p>
                </button>
              </div>
            </div>
            
            <div className="text-center">
              <button 
                className="btn btn-cta fw-bold py-2 rounded-5 mt-auto"
                  onClick={() => {
                  // Handle multiple addon selection - only add newly selected addons
                    selectedAddons.forEach(addon => {
                      const addonProduct = {
                        type: "addon",
                        pid: addon.id,
                        name: addon.name,
                        billingcycle: addon.is_one_time ? 'onetime' : currentService.billingcycle.toLowerCase(),
                        price: getAddonPrice(addon, currentService.billingcycle).price,
                        serviceid: currentService.serviceid || currentService.id,
                        currentService: currentService,
                        addonType: addon.is_one_time ? "one_time" : "recurring",
                        addonDetails: {
                          addonName: addon.name,
                          addonDescription: addon.description,
                          addonCategory: addon.category,
                          billingCycle: addon.is_one_time ? 'onetime' : currentService.billingcycle.toLowerCase(),
                          addonPrice: getAddonPrice(addon, currentService.billingcycle).price,
                          isOneTime: addon.is_one_time,
                          isRecurring: addon.is_recurring
                        },
                      };
                      dispatch(addToCart(addonProduct));
                    });
                    notifySuccess(`${selectedAddons.length} addon berhasil ditambahkan ke cart`);
                    setSelectedAddons([]);
                  }}
                disabled={selectedAddons.length === 0}
                >
                Tambahkan ke Cart ({selectedAddons.length})
              </button>
            </div>
          </CardWrapper>
        )}


        {/* Show message when no addons available */}
        {currentService && serviceid && currentService.status === 'Active' && Object.keys(availableAddons).length === 0 && (
          <CardWrapper title="Addon Layanan">
            <div className="text-center py-5">
              <div className="mb-4">
                <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                  <FontAwesomeIcon icon={faInfoCircle} size="2x" className="text-info" />
                </div>
                <h5 className="text-muted">Tidak Ada Addon Tersedia</h5>
                <p className="text-muted">
                  Tidak ada addon yang tersedia untuk paket ini.
                  <br />
                  <small className="text-muted">
                    Silakan hubungi support untuk informasi lebih lanjut tentang addon yang tersedia.
                  </small>
                </p>
              </div>
            </div>
          </CardWrapper>
        )}
      </div>
    </MainLayouts>
  );
};

export default Addon;
